import {
  Fragment,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaEdit, FaSignOutAlt, FaTrashAlt } from "react-icons/fa";
import config from "../config";
import {
  formatPreviewCaregiverLocation,
  formatPreviewRole,
  clearPreviewEditSession,
  getPreviewOwnerKey,
  isValidPreviewEmail,
  normalizePreviewEmail,
  readOwnedCaregiverTokens,
  readPreviewEditSession,
  writePreviewEditSession,
  writeOwnedCaregiverTokens
} from "../utils/previewMap";

const defaultWorldCenter = [12, 8];
const defaultWorldZoom = 2;

const yellowIcon = L.divIcon({
  className: "care-map-marker-shell",
  html: `
    <div class="care-map-marker care-map-marker-you" aria-label="Caregiver profile">
      <span>C</span>
    </div>
  `,
  iconSize: [46, 54],
  iconAnchor: [23, 50],
  popupAnchor: [0, -48]
});

const getCoordinateKey = (latitude, longitude) =>
  `${Number(latitude).toFixed(5)}:${Number(longitude).toFixed(5)}`;

const getStableCaregiverKey = (caregiver = {}) =>
  String(caregiver.caregiverID || caregiver.workerID || getCaregiverDisplayName(caregiver));

const getOffsetPosition = (latitude, longitude, index, total) => {
  const safeLatitude = Number(latitude);

  if (total <= 1) {
    return [safeLatitude, Number(longitude)];
  }

  const radius = Math.min(0.02, 0.006 + total * 0.0012);
  const angle = (index / total) * Math.PI * 2;
  const latitudeOffset = Math.sin(angle) * radius;
  const longitudeOffset =
    (Math.cos(angle) * radius) / Math.max(Math.cos((safeLatitude * Math.PI) / 180), 0.35);

  return [safeLatitude + latitudeOffset, Number(longitude) + longitudeOffset];
};

const spreadOverlappingMarkers = (items) => {
  const grouped = items.reduce((acc, item) => {
    const key = getCoordinateKey(item.latitude, item.longitude);

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);
    return acc;
  }, {});

  return Object.values(grouped).flatMap((group) =>
    [...group].sort((left, right) =>
      getStableCaregiverKey(left).localeCompare(getStableCaregiverKey(right))
    ).map((item, index) => {
      const overlapGroupKey = getCoordinateKey(item.latitude, item.longitude);
      const [latitude, longitude] = getOffsetPosition(
        item.latitude,
        item.longitude,
        index,
        group.length
      );

      return {
        ...item,
        markerLatitude: latitude,
        markerLongitude: longitude,
        overlapCount: group.length,
        originLatitude: Number(item.latitude),
        originLongitude: Number(item.longitude),
        overlapGroupKey,
        isOverlapping: group.length > 1
      };
    })
  );
};

const buildOverlapGuides = (markers) =>
  Object.values(
    markers.reduce((acc, marker) => {
      if (!marker.isOverlapping) {
        return acc;
      }

      if (!acc[marker.overlapGroupKey]) {
        acc[marker.overlapGroupKey] = {
          key: marker.overlapGroupKey,
          count: marker.overlapCount,
          center: [marker.originLatitude, marker.originLongitude],
          members: []
        };
      }

      acc[marker.overlapGroupKey].members.push(marker);
      return acc;
    }, {})
  );

const hasValidCoordinates = (caregiver) => {
  const latitude = Number(caregiver?.latitude);
  const longitude = Number(caregiver?.longitude);

  return Number.isFinite(latitude) && Number.isFinite(longitude);
};

const getCaregiverDisplayName = (caregiver = {}) =>
  `${caregiver.firstName || ""} ${caregiver.lastName || ""}`.replace(/\s+/g, " ").trim() ||
  "Caregiver";

const formatPaymentMethod = (value) =>
  ({
    credit_card: "Credit Card",
    mobile_money: "Mobile Money",
    cash: "Cash",
    bank_transfer: "Bank Transfer"
  }[String(value || "").trim()] || value);

const formatGender = (value) =>
  ({
    F: "Female",
    M: "Male"
  }[String(value || "").trim()] || value);

const getCaregiverSummaryFields = (caregiver = {}) =>
  [
    { label: "Gender", value: formatGender(caregiver.gender) },
    { label: "Age", value: caregiver.age },
    { label: "Language", value: caregiver.spokenLanguage || caregiver.language },
    { label: "Country", value: caregiver.country },
    { label: "City", value: caregiver.city },
    { label: "Payment", value: formatPaymentMethod(caregiver.paymentMethod || caregiver.paymentMode) },
    { label: "Rate", value: caregiver.rate ? `$${caregiver.rate}/hr` : "" },
    {
      label: "Availability",
      value: caregiver.status === "unavailable" ? "Unavailable" : "Available"
    },
    {
      label: "Experience",
      value:
        caregiver.yearsExperience !== undefined &&
        caregiver.yearsExperience !== null &&
        caregiver.yearsExperience !== ""
          ? `${caregiver.yearsExperience} years`
          : ""
    },
    { label: "Specialties", value: caregiver.specialties },
    { label: "Certifications", value: caregiver.certifications }
  ].filter((field) => String(field.value ?? "").trim());

const DetailItem = ({ label, value }) => (
  <div style={popupDetailItem}>
    <span style={popupDetailLabel}>{label}</span>
    <span style={popupDetailValue}>{value}</span>
  </div>
);

const PreviewMap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const markerRefs = useRef({});
  const searchBlurTimerRef = useRef(null);
  const [caregivers, setCaregivers] = useState([]);
  const [ownedCaregiverTokens, setOwnedCaregiverTokens] = useState(
    readOwnedCaregiverTokens
  );
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(location.state?.successMessage || "");
  const [focusCaregiverID, setFocusCaregiverID] = useState(
    location.state?.focusCaregiverID || ""
  );
  const [selectedCaregiverID, setSelectedCaregiverID] = useState(
    location.state?.focusCaregiverID || ""
  );
  const [emailGate, setEmailGate] = useState({
    caregiverID: "",
    caregiverName: "",
    email: "",
    password: "",
    newPassword: "",
    confirmPassword: "",
    hasPassword: false,
    step: "credentials",
    intent: "view"
  });
  const [emailGateError, setEmailGateError] = useState("");
  const [emailGateSubmitting, setEmailGateSubmitting] = useState(false);
  const [hasActiveCaregiverSession, setHasActiveCaregiverSession] = useState(
    () => Boolean(readPreviewEditSession()?.token)
  );
  const [mapInstance, setMapInstance] = useState(null);
  const [mapZoom, setMapZoom] = useState(defaultWorldZoom);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const persistOwnedCaregiverTokens = (updater) => {
    setOwnedCaregiverTokens((previous) => {
      const nextValue =
        typeof updater === "function" ? updater(previous) : updater;

      writeOwnedCaregiverTokens(nextValue);
      return nextValue;
    });
  };

  const loadCaregivers = useCallback(async () => {
    try {
      const ownerKey = getPreviewOwnerKey();
      const response = await axios.get(`${config.API_BASE_URL}/caregiver-temp-map`, {
        headers: {
          "x-preview-owner-key": ownerKey
        }
      });
      const nextCaregivers = response.data.caregivers || [];
      setCaregivers((previous) => {
        const unlockedById = new Map(
          previous
            .filter((caregiver) => caregiver.isUnlocked)
            .map((caregiver) => [caregiver.caregiverID, caregiver])
        );

        return nextCaregivers.map((summary) => {
          const unlocked = unlockedById.get(summary.caregiverID);

          if (!unlocked) {
            return summary;
          }

          return {
            ...summary,
            ...unlocked,
            hasEmail: summary.hasEmail,
            hasPassword: summary.hasPassword || unlocked.hasPassword,
            isOwned: summary.isOwned,
            isUnlocked: true
          };
        });
      });

      persistOwnedCaregiverTokens((previous) => {
        const visibleIds = new Set(nextCaregivers.map((caregiver) => caregiver.caregiverID));
        return Object.fromEntries(
          Object.entries(previous).filter(([caregiverID]) => visibleIds.has(caregiverID))
        );
      });
    } catch {
      setError("Unable to load map.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCaregivers();

    const interval = window.setInterval(loadCaregivers, 3000);
    const handleFocus = () => loadCaregivers();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadCaregivers();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadCaregivers]);

  useEffect(() => {
    if (location.state?.successMessage || location.state?.focusCaregiverID) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(
    () => () => {
      if (searchBlurTimerRef.current) {
        window.clearTimeout(searchBlurTimerRef.current);
      }
    },
    []
  );

  const mapCaregivers = useMemo(
    () =>
      spreadOverlappingMarkers(
        caregivers
          .filter(hasValidCoordinates)
          .map((caregiver) => ({
            ...caregiver,
            canManage: Boolean(
              caregiver.isOwned || ownedCaregiverTokens[caregiver.caregiverID]
            )
          }))
      ),
    [caregivers, ownedCaregiverTokens]
  );

  const mapPoints = useMemo(
    () =>
      mapCaregivers.map((caregiver) => [
        caregiver.markerLatitude,
        caregiver.markerLongitude
      ]),
    [mapCaregivers]
  );

  const overlapGuides = useMemo(
    () => buildOverlapGuides(mapCaregivers),
    [mapCaregivers]
  );

  const normalizedSearchTerm = deferredSearchTerm.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!normalizedSearchTerm) {
      return [];
    }

    return mapCaregivers
      .map((caregiver) => {
        const displayName = getCaregiverDisplayName(caregiver);
        const normalizedName = displayName.toLowerCase();
        const matchIndex = normalizedName.indexOf(normalizedSearchTerm);

        if (matchIndex === -1) {
          return null;
        }

        return {
          caregiver,
          displayName,
          matchIndex,
          startsWithMatch: normalizedName.startsWith(normalizedSearchTerm)
        };
      })
      .filter(Boolean)
      .sort(
        (left, right) =>
          Number(right.startsWithMatch) - Number(left.startsWithMatch) ||
          left.matchIndex - right.matchIndex ||
          left.displayName.localeCompare(right.displayName)
      )
      .slice(0, 6);
  }, [mapCaregivers, normalizedSearchTerm]);

  const selectedCaregiver = useMemo(
    () =>
      mapCaregivers.find((caregiver) => caregiver.caregiverID === selectedCaregiverID) || null,
    [mapCaregivers, selectedCaregiverID]
  );

  const hasUnlockedCaregiver = useMemo(
    () => caregivers.some((caregiver) => caregiver.isUnlocked),
    [caregivers]
  );
  const canCaregiverLogout = hasUnlockedCaregiver || hasActiveCaregiverSession;

  useEffect(() => {
    if (!focusCaregiverID || !mapCaregivers.length) return;

    const marker = markerRefs.current[focusCaregiverID];
    if (!marker) return;

    const timer = window.setTimeout(() => {
      marker.openPopup();
      setSelectedCaregiverID(focusCaregiverID);
      setFocusCaregiverID("");
    }, 250);

    return () => window.clearTimeout(timer);
  }, [focusCaregiverID, mapCaregivers]);

  const focusCaregiver = useCallback(
    (caregiver) => {
      if (!caregiver) return;

      setSelectedCaregiverID(caregiver.caregiverID);

      if (mapInstance) {
        mapInstance.flyTo(
          [caregiver.markerLatitude, caregiver.markerLongitude],
          caregiver.isOverlapping
            ? Math.max(mapInstance.getZoom(), 6)
            : Math.max(mapInstance.getZoom(), 8),
          {
            animate: true,
            duration: 0.8
          }
        );
      }

      const marker = markerRefs.current[caregiver.caregiverID];
      if (marker) {
        window.setTimeout(() => marker.openPopup(), 140);
      }
    },
    [mapInstance]
  );

  const fetchUnlockedCaregiver = useCallback(async (caregiverID, token) => {
    const response = await axios.get(
      `${config.API_BASE_URL}/caregiver-temp-map/${caregiverID}`,
      {
        headers: {
          "x-preview-edit-session": token
        }
      }
    );
    const unlockedCaregiver = {
      ...response.data.caregiver,
      isUnlocked: true
    };

    setCaregivers((previous) =>
      previous.map((caregiver) =>
        caregiver.caregiverID === caregiverID
          ? {
              ...caregiver,
              ...unlockedCaregiver,
              isOwned: caregiver.isOwned,
              isUnlocked: true
            }
          : caregiver
      )
    );
    setSelectedCaregiverID(caregiverID);
    setFocusCaregiverID(caregiverID);
    setHasActiveCaregiverSession(true);

    return unlockedCaregiver;
  }, []);

  const openCaregiverProfile = useCallback(
    async (caregiver, intent = "view") => {
      if (!caregiver) return;

      setSelectedCaregiverID(caregiver.caregiverID);

      if (caregiver.isUnlocked) {
        if (intent === "edit") {
          navigate(`/realMap/edit/${caregiver.caregiverID}`);
        } else {
          focusCaregiver(caregiver);
        }
        return;
      }

      if (!caregiver.hasEmail) {
        setError("This profile needs an email address before it can be viewed.");
        return;
      }

      const activeSession = readPreviewEditSession();
      if (
        activeSession?.token &&
        activeSession.caregiverID === caregiver.caregiverID
      ) {
        try {
          await fetchUnlockedCaregiver(caregiver.caregiverID, activeSession.token);
          if (intent === "edit") {
            navigate(`/realMap/edit/${caregiver.caregiverID}`);
          }
          return;
        } catch {
          clearPreviewEditSession(caregiver.caregiverID);
          setHasActiveCaregiverSession(false);
        }
      }

      setError("");
      setEmailGateError("");
      setSuccess("");
      setEmailGate({
        caregiverID: caregiver.caregiverID,
        caregiverName: getCaregiverDisplayName(caregiver),
        email: "",
        password: "",
        newPassword: "",
        confirmPassword: "",
        hasPassword: Boolean(caregiver.hasPassword),
        step: caregiver.hasPassword ? "credentials" : "email",
        intent
      });
    },
    [fetchUnlockedCaregiver, focusCaregiver, navigate]
  );

  const handleSearchSelect = useCallback(
    (caregiver) => {
      setSearchTerm(getCaregiverDisplayName(caregiver));
      setSearchOpen(false);
      focusCaregiver(caregiver);
      if (!caregiver.isUnlocked) {
        void openCaregiverProfile(caregiver);
      }
    },
    [focusCaregiver, openCaregiverProfile]
  );

  const handleDelete = async (caregiver) => {
    const deleteToken = ownedCaregiverTokens[caregiver.caregiverID];
    const ownerKey = getPreviewOwnerKey();

    if (!caregiver.canManage) {
      setError("You can only delete your own caregiver preview.");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${caregiver.firstName} ${caregiver.lastName} from the map?`
    );

    if (!confirmed) return;

    setDeletingId(caregiver.caregiverID);
    setError("");
    setSuccess("");

    try {
      await axios.delete(
        `${config.API_BASE_URL}/caregiver-temp-map/${caregiver.caregiverID}`,
        {
          headers: {
            ...(deleteToken ? { "x-temp-delete-token": deleteToken } : {}),
            "x-preview-owner-key": ownerKey
          }
        }
      );

      setCaregivers((previous) =>
        previous.filter((entry) => entry.caregiverID !== caregiver.caregiverID)
      );
      persistOwnedCaregiverTokens((previous) => {
        const nextValue = { ...previous };
        delete nextValue[caregiver.caregiverID];
        return nextValue;
      });
      setSelectedCaregiverID((previous) =>
        previous === caregiver.caregiverID ? "" : previous
      );
      setSuccess("Caregiver removed.");
    } catch (deleteError) {
      setError(
        deleteError.response?.data?.error ||
          "Unable to delete caregiver."
      );
    } finally {
      setDeletingId("");
    }
  };

  const handleEdit = (caregiver) => {
    void openCaregiverProfile(caregiver, "edit");
  };

  const handleCaregiverLogout = async () => {
    const activeSession = readPreviewEditSession();

    clearPreviewEditSession();
    setHasActiveCaregiverSession(false);
    setEmailGateError("");
    setEmailGateSubmitting(false);
    setEmailGate({
      caregiverID: "",
      caregiverName: "",
      email: "",
      password: "",
      newPassword: "",
      confirmPassword: "",
      hasPassword: false,
      step: "credentials",
      intent: "view"
    });
    setCaregivers((previous) =>
      previous.map((caregiver) => ({
        caregiverID: caregiver.caregiverID,
        firstName: caregiver.firstName || "",
        lastName: caregiver.lastName || "",
        country: caregiver.country || "",
        city: caregiver.city || "",
        latitude: caregiver.originLatitude ?? caregiver.latitude,
        longitude: caregiver.originLongitude ?? caregiver.longitude,
        locationPrecision: caregiver.locationPrecision || "",
        locationQuery: caregiver.locationQuery || "",
        countrySource: caregiver.countrySource || "",
        hasEmail: Boolean(caregiver.hasEmail),
        hasPassword: Boolean(caregiver.hasPassword),
        isOwned: Boolean(caregiver.isOwned)
      }))
    );
    setSuccess("Caregiver logged out.");
    setError("");

    if (!activeSession?.token) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/caregiver-temp-map/edit-session`, {
        headers: {
          "x-preview-edit-session": activeSession.token
        }
      });
    } catch {
      // The browser session has already been cleared.
    }
  };

  const closeEmailGate = (force = false) => {
    const shouldForce = force === true;
    if (emailGateSubmitting && !shouldForce) return;

    setEmailGateError("");
    setEmailGate({
      caregiverID: "",
      caregiverName: "",
      email: "",
      password: "",
      newPassword: "",
      confirmPassword: "",
      hasPassword: false,
      step: "credentials",
      intent: "view"
    });
  };

  const submitEmailGate = async (event) => {
    event.preventDefault();

    const normalizedEmail = normalizePreviewEmail(emailGate.email);

    if (!normalizedEmail) {
      setEmailGateError("Enter the email address linked to this profile.");
      return;
    }

    if (!isValidPreviewEmail(normalizedEmail)) {
      setEmailGateError("Enter a valid email address.");
      return;
    }

    if (!emailGate.hasPassword && emailGate.step === "email") {
      try {
        setEmailGateSubmitting(true);
        setEmailGateError("");
        const response = await axios.post(
          `${config.API_BASE_URL}/caregiver-temp-map/${emailGate.caregiverID}/edit-session`,
          {
            email: normalizedEmail,
            verifyEmailOnly: true
          }
        );

        setEmailGate((previous) => ({
          ...previous,
          email: normalizedEmail,
          hasPassword: !response.data.requiresPasswordSetup,
          step: response.data.requiresPasswordSetup ? "createPassword" : "credentials"
        }));
      } catch (submitError) {
        setEmailGateError(
          submitError.response?.data?.error ||
            "Unable to verify that email address."
        );
      } finally {
        setEmailGateSubmitting(false);
      }
      return;
    }

    if (emailGate.hasPassword && !emailGate.password) {
      setEmailGateError("Enter the password for this profile.");
      return;
    }

    if (!emailGate.hasPassword) {
      if (!emailGate.newPassword) {
        setEmailGateError("Create a password before viewing this profile.");
        return;
      }

      if (emailGate.newPassword.length < 8) {
        setEmailGateError("Password must be at least 8 characters.");
        return;
      }

      if (emailGate.newPassword !== emailGate.confirmPassword) {
        setEmailGateError("Password and confirmation must match.");
        return;
      }
    }

    try {
      setEmailGateSubmitting(true);
      setEmailGateError("");
      const response = await axios.post(
        `${config.API_BASE_URL}/caregiver-temp-map/${emailGate.caregiverID}/edit-session`,
        {
          email: normalizedEmail,
          password: emailGate.password,
          newPassword: emailGate.hasPassword ? "" : emailGate.newPassword
        }
      );

      writePreviewEditSession({
        caregiverID: emailGate.caregiverID,
        token: response.data.editSessionToken
      });

      await fetchUnlockedCaregiver(emailGate.caregiverID, response.data.editSessionToken);
      const shouldEdit = emailGate.intent === "edit";

      closeEmailGate(true);
      setError("");
      setSuccess("");
      if (shouldEdit) {
        navigate(`/realMap/edit/${emailGate.caregiverID}`);
      }
    } catch (submitError) {
      clearPreviewEditSession(emailGate.caregiverID);
      setEmailGateError(
        submitError.response?.data?.error ||
          "Unable to unlock that caregiver profile."
      );
    } finally {
      setEmailGateSubmitting(false);
    }
  };

  return (
    <div style={page}>
      <style>{responsiveStyles}</style>

      <div style={shell}>
        {emailGate.caregiverID && (
          <div style={modalBackdrop} onClick={closeEmailGate}>
            <div style={modalCard} onClick={(event) => event.stopPropagation()}>
              <div style={modalBadge}>Profile access</div>
              <h2 style={modalTitle}>
                {emailGate.intent === "edit" ? "Edit" : "View"}{" "}
                {emailGate.caregiverName || "this profile"}
              </h2>
              <p style={modalText}>
                {emailGate.hasPassword
                  ? "Enter the email address and password for this caregiver profile."
                  : emailGate.step === "email"
                    ? "Enter the email address linked to this caregiver profile first."
                    : "Create a password for this caregiver profile before continuing."}
              </p>

              <form style={modalForm} onSubmit={submitEmailGate}>
                <input
                  style={modalInput}
                  type="email"
                  value={emailGate.email}
                  disabled={!emailGate.hasPassword && emailGate.step === "createPassword"}
                  onChange={(event) =>
                    setEmailGate((previous) => ({
                      ...previous,
                      email: event.target.value
                    }))
                  }
                  placeholder="you@example.com"
                  autoComplete="email"
                />

                {emailGate.hasPassword && (
                  <input
                    style={modalInput}
                    type="password"
                    value={emailGate.password}
                    onChange={(event) =>
                      setEmailGate((previous) => ({
                        ...previous,
                        password: event.target.value
                      }))
                    }
                    placeholder="Password"
                    autoComplete="current-password"
                  />
                )}

                {!emailGate.hasPassword && emailGate.step === "createPassword" && (
                  <>
                    <input
                      style={modalInput}
                      type="password"
                      value={emailGate.newPassword}
                      onChange={(event) =>
                        setEmailGate((previous) => ({
                          ...previous,
                          newPassword: event.target.value
                        }))
                      }
                      placeholder="Create password"
                      autoComplete="new-password"
                    />

                    <input
                      style={modalInput}
                      type="password"
                      value={emailGate.confirmPassword}
                      onChange={(event) =>
                        setEmailGate((previous) => ({
                          ...previous,
                          confirmPassword: event.target.value
                        }))
                      }
                      placeholder="Confirm password"
                      autoComplete="new-password"
                    />
                  </>
                )}

                {emailGateError && <div style={modalError}>{emailGateError}</div>}

                <div style={modalActions}>
                  <button
                    type="button"
                    style={modalSecondaryButton}
                    onClick={closeEmailGate}
                    disabled={emailGateSubmitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={modalPrimaryButton} disabled={emailGateSubmitting}>
                    {emailGateSubmitting
                      ? "Checking..."
                      : emailGate.hasPassword
                        ? "Continue"
                        : emailGate.step === "email"
                          ? "Continue"
                          : "Create password"}
                  </button>
                </div>
              </form>
          </div>

        </div>
        )}

        <div style={topBar} className="preview-map-top-bar">
          <div style={searchWrap}>
            <div style={searchCard}>
              <label htmlFor="preview-map-search" style={searchLabel}>
                Search by names
              </label>

              <div style={searchInputRow}>
                <input
                  id="preview-map-search"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => {
                    if (searchBlurTimerRef.current) {
                      window.clearTimeout(searchBlurTimerRef.current);
                    }
                    setSearchOpen(true);
                  }}
                  onBlur={() => {
                    searchBlurTimerRef.current = window.setTimeout(() => {
                      setSearchOpen(false);
                    }, 120);
                  }}
                  placeholder="Type a first or last name"
                  autoComplete="off"
                  aria-label="Search caregivers by name"
                  style={searchInput}
                />

                {searchTerm && (
                  <button
                    type="button"
                    style={searchClearButton}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setSearchTerm("");
                      setSearchOpen(false);
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              {searchOpen && normalizedSearchTerm && (
                <div style={searchResultsPanel}>
                  {searchResults.length ? (
                    searchResults.map(({ caregiver, displayName }) => (
                      <button
                        key={caregiver.caregiverID}
                        type="button"
                        style={searchResultButton}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSearchSelect(caregiver)}
                      >
                        <span style={searchResultName}>{displayName}</span>
                        <span style={searchResultMeta}>
                          {formatPreviewCaregiverLocation(caregiver) || "Pinned map location"}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div style={searchEmptyState}>No caregiver found with that name.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {canCaregiverLogout && (
            <button
              type="button"
              style={logoutButton}
              onClick={handleCaregiverLogout}
            >
              <FaSignOutAlt />
              Logout
            </button>
          )}

        </div>

        {(error || success) && (
          <section style={bannerRow}>
            {error && <div style={errorBanner}>{error}</div>}
            {success && <div style={successBanner}>{success}</div>}
          </section>
        )}

        <section style={mapCard}>
          <div style={mapShell}>
            <div style={zoomControlsWrap}>
              <button
                type="button"
                style={{
                  ...zoomButton,
                  ...(mapInstance && mapZoom <= defaultWorldZoom ? zoomButtonDisabled : {})
                }}
                onClick={() => mapInstance?.zoomOut()}
                disabled={!mapInstance || mapZoom <= defaultWorldZoom}
                aria-label="Zoom out"
                title="Zoom out"
              >
                -
              </button>

              <div style={zoomLevelBadge}>Zoom {mapZoom}</div>

              <button
                type="button"
                style={zoomButton}
                onClick={() => mapInstance?.zoomIn()}
                disabled={!mapInstance}
                aria-label="Zoom in"
                title="Zoom in"
              >
                +
              </button>
            </div>

          <MapContainer
            center={defaultWorldCenter}
            zoom={defaultWorldZoom}
            minZoom={2}
            scrollWheelZoom
            zoomControl={false}
            style={mapStyle}
            className="preview-map-canvas"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <MapViewportManager
              loading={loading}
              points={mapPoints}
              selectedCaregiver={selectedCaregiver}
              onMapReady={setMapInstance}
              onZoomChange={setMapZoom}
            />

            {overlapGuides.map((group) => (
              <Fragment key={`overlap-${group.key}`}>
                {group.members.map((caregiver) => (
                  <Polyline
                    key={`overlap-line-${caregiver.caregiverID}`}
                    positions={[
                      group.center,
                      [caregiver.markerLatitude, caregiver.markerLongitude]
                    ]}
                    pathOptions={{
                      color: "#d4a017",
                      weight: 2,
                      opacity: 0.55,
                      dashArray: "4 6"
                    }}
                  />
                ))}

                <CircleMarker
                  center={group.center}
                  radius={Math.min(10 + group.count, 16)}
                  pathOptions={{
                    color: "#8a5d00",
                    weight: 2,
                    fillColor: "#ffffff",
                    fillOpacity: 0.96
                  }}
                >
                  <Popup maxWidth={240} className="preview-map-popup">
                    <div style={sharedPopup}>
                      {group.count} caregivers share this point.
                    </div>
                  </Popup>
                </CircleMarker>
              </Fragment>
            ))}

            {mapCaregivers.map((caregiver) => {
              const locationLabel =
                formatPreviewCaregiverLocation(caregiver) || "Pinned map location";
              const summaryFields = getCaregiverSummaryFields(caregiver);
              const contactPhone = caregiver.phone || caregiver.whatsapp || "";

              return (
                <Marker
                  key={caregiver.caregiverID}
                  position={[caregiver.markerLatitude, caregiver.markerLongitude]}
                  icon={yellowIcon}
                  ref={(marker) => {
                    if (marker) {
                      markerRefs.current[caregiver.caregiverID] = marker;
                    }
                  }}
                  eventHandlers={{
                    click: () => {
                      setSelectedCaregiverID(caregiver.caregiverID);
                      if (!caregiver.isUnlocked) {
                        void openCaregiverProfile(caregiver);
                      }
                    }
                  }}
                >
                  <Popup maxWidth={640} className="preview-map-popup">
                    <div style={popupCard} className="preview-caregiver-popup-card">
                      {!caregiver.isUnlocked ? (
                        <div style={popupBody}>
                          <div style={popupName}>
                            {caregiver.firstName} {caregiver.lastName}
                          </div>

                          <div style={detailCard}>
                            <div style={detailCardTitle}>Location</div>
                            {locationLabel}
                          </div>

                          <div style={warningCard}>
                            Unlock this profile with the caregiver email before viewing details.
                          </div>

                          <div style={popupActionRow}>
                            <button
                              type="button"
                              style={popupEditButton}
                              onClick={() => openCaregiverProfile(caregiver)}
                            >
                              View profile
                            </button>

                            {caregiver.canManage && (
                              <button
                                type="button"
                                style={popupDeleteButton}
                                onClick={() => handleDelete(caregiver)}
                                disabled={deletingId === caregiver.caregiverID}
                              >
                                <FaTrashAlt />
                                {deletingId === caregiver.caregiverID ? "Deleting..." : "Delete"}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          {caregiver.profilePhoto ? (
                            <img
                              src={caregiver.profilePhoto}
                              alt={`${caregiver.firstName} ${caregiver.lastName}`}
                              style={popupPhoto}
                            />
                          ) : (
                            <div style={popupFallback}>
                              {`${caregiver.firstName?.[0] || ""}${caregiver.lastName?.[0] || ""}`.toUpperCase() || "CG"}
                            </div>
                          )}

                          <div style={popupBody}>
                            <div style={popupName}>
                              {caregiver.firstName} {caregiver.lastName}
                            </div>

                        {caregiver.professionalTitle && (
                          <div style={popupTitle}>{caregiver.professionalTitle}</div>
                        )}

                        {(caregiver.role || contactPhone) && (
                          <div style={popupMetaRow}>
                            {caregiver.role && (
                              <span style={popupRoleBadge}>
                                {formatPreviewRole(caregiver.role)}
                              </span>
                            )}
                            {contactPhone && (
                              <span style={popupPhoneText}>{contactPhone}</span>
                            )}
                          </div>
                        )}

                        <div style={detailCard}>
                          <div style={detailCardTitle}>Location</div>
                          {locationLabel}
                        </div>

                        {summaryFields.length > 0 && (
                          <div style={popupDetailsGrid}>
                            {summaryFields.map((field) => (
                              <DetailItem
                                key={`${field.label}:${field.value}`}
                                label={field.label}
                                value={field.value}
                              />
                            ))}
                          </div>
                        )}

                        {caregiver.bio && (
                          <div style={popupTextBlock}>
                            <div style={popupTextLabel}>Bio</div>
                            {caregiver.bio}
                          </div>
                        )}

                        {caregiver.availabilityNotes && (
                          <div style={popupTextBlock}>
                            <div style={popupTextLabel}>Availability notes</div>
                            {caregiver.availabilityNotes}
                          </div>
                        )}

                        {Array.isArray(caregiver.experienceEntries) &&
                          caregiver.experienceEntries.length > 0 && (
                            <div style={popupTextBlock}>
                              <div style={popupTextLabel}>Work history</div>
                              <div style={experienceList}>
                                {caregiver.experienceEntries.slice(0, 3).map((entry, index) => (
                                  <div key={`${entry.employer || "entry"}-${index}`} style={experienceItem}>
                                    <strong>{entry.role || entry.employer || "Experience"}</strong>
                                    {(entry.employer || entry.period) && (
                                      <span>
                                        {[entry.employer, entry.period].filter(Boolean).join(" - ")}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {caregiver.countrySource === "needs-confirmation" && (
                          <div style={warningCard}>
                            Country still needs confirmation. This profile was
                            imported from the CSV and placed approximately for now.
                          </div>
                        )}

                        <div style={popupActionRow}>
                          {contactPhone && caregiver.status !== "unavailable" && (
                            <a
                              href={`https://wa.me/${String(contactPhone).replace(/\D/g, "")}`}
                              target="_blank"
                              rel="noreferrer"
                              style={popupContactButton}
                            >
                              Contact
                            </a>
                          )}

                          <button
                            type="button"
                            style={popupAppointmentsButton}
                            onClick={() => navigate(`/appointments/${caregiver.caregiverID}`)}
                          >
                            Connected elders
                          </button>

                          {caregiver.hasEmail && (
                            <button
                              type="button"
                              style={popupEditButton}
                              onClick={() => handleEdit(caregiver)}
                            >
                              <FaEdit />
                              Edit
                            </button>
                          )}

                          {caregiver.canManage && (
                            <button
                              type="button"
                              style={popupDeleteButton}
                              onClick={() => handleDelete(caregiver)}
                              disabled={deletingId === caregiver.caregiverID}
                            >
                              <FaTrashAlt />
                              {deletingId === caregiver.caregiverID ? "Deleting..." : "Delete"}
                            </button>
                          )}
                        </div>

                        {!caregiver.hasEmail && (
                          <div style={warningCard}>
                            This profile still needs an email address before email-protected
                            editing can be used.
                          </div>
                        )}
                          </div>
                        </>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
          </div>
        </section>
      </div>
    </div>
  );
};

const MapViewportManager = ({
  loading,
  points,
  selectedCaregiver,
  onMapReady,
  onZoomChange
}) => {
  const map = useMap();
  const lastBoundsSignatureRef = useRef("");
  const lastSelectedSignatureRef = useRef("");

  useEffect(() => {
    onMapReady(map);
    onZoomChange(map.getZoom());

    const syncZoom = () => onZoomChange(map.getZoom());
    map.on("zoomend", syncZoom);

    return () => {
      map.off("zoomend", syncZoom);
      onMapReady(null);
    };
  }, [map, onMapReady, onZoomChange]);

  useEffect(() => {
    if (loading) return;

    if (!points.length) {
      if (lastBoundsSignatureRef.current !== "empty") {
        map.setView(defaultWorldCenter, defaultWorldZoom);
        lastBoundsSignatureRef.current = "empty";
      }
      return;
    }

    const nextSignature = points
      .map(([latitude, longitude]) => `${latitude.toFixed(5)}:${longitude.toFixed(5)}`)
      .join("|");

    if (lastBoundsSignatureRef.current === nextSignature) {
      return;
    }

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, {
      padding: [56, 56],
      maxZoom: points.length === 1 ? 8 : 6
    });
    lastBoundsSignatureRef.current = nextSignature;
  }, [loading, map, points]);

  useEffect(() => {
    if (!selectedCaregiver) return;

    const nextSignature = [
      selectedCaregiver.caregiverID,
      selectedCaregiver.markerLatitude.toFixed(5),
      selectedCaregiver.markerLongitude.toFixed(5)
    ].join(":");

    if (lastSelectedSignatureRef.current === nextSignature) {
      return;
    }

    map.flyTo(
      [selectedCaregiver.markerLatitude, selectedCaregiver.markerLongitude],
      selectedCaregiver.isOverlapping ? Math.max(map.getZoom(), 6) : Math.max(map.getZoom(), 8),
      {
        animate: true,
        duration: 0.8
      }
    );
    lastSelectedSignatureRef.current = nextSignature;
  }, [map, selectedCaregiver]);

  return null;
};

export default PreviewMap;

const responsiveStyles = `
  .preview-map-canvas {
    height: calc(100vh - 120px) !important;
    min-height: 760px !important;
    background: #dbeafe;
  }

  .care-map-marker-shell {
    background: transparent;
    border: none;
  }

  .care-map-marker {
    position: relative;
    width: 46px;
    height: 46px;
    border-radius: 16px 16px 16px 4px;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #ffffff;
    box-shadow: 0 14px 26px rgba(15, 23, 42, 0.24);
  }

  .care-map-marker::after {
    content: "";
    position: absolute;
    inset: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
  }

  .care-map-marker span {
    position: relative;
    z-index: 1;
    transform: rotate(45deg);
    color: #ffffff;
    font-size: 0.88rem;
    font-weight: 900;
    line-height: 1;
  }

  .care-map-marker-you {
    background: linear-gradient(135deg, #d97706, #fbbf24);
  }

  .preview-map-popup .leaflet-popup-content-wrapper {
    border-radius: 24px !important;
    padding: 0 !important;
    overflow: hidden;
    box-shadow: 0 22px 40px rgba(15, 23, 42, 0.18) !important;
  }

  .preview-map-popup .leaflet-popup-content {
    margin: 0 !important;
    width: 560px !important;
  }

  .preview-map-popup .leaflet-popup-close-button {
    top: 10px !important;
    right: 10px !important;
    width: 30px !important;
    height: 30px !important;
    border-radius: 999px !important;
    background: rgba(255, 255, 255, 0.96) !important;
    color: #102a43 !important;
    font-size: 22px !important;
    line-height: 28px !important;
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.18) !important;
  }

  .preview-caregiver-popup-card > div:only-child {
    grid-column: 1 / -1;
  }

  @media (max-width: 980px) {
    .preview-map-top-bar {
      display: grid !important;
      grid-template-columns: 1fr !important;
    }

    .preview-map-top-bar > * {
      width: 100% !important;
    }

    .preview-map-canvas {
      height: 78vh !important;
      min-height: 560px !important;
    }

    .preview-map-popup .leaflet-popup-content {
      width: min(92vw, 560px) !important;
    }

    .preview-caregiver-popup-card {
      max-height: none;
      overflow: visible;
      grid-template-columns: 118px minmax(0, 1fr) !important;
    }
  }

  @media (max-width: 760px) {
    .preview-map-canvas {
      height: 72svh !important;
      min-height: 430px !important;
    }

    .preview-map-popup .leaflet-popup-content {
      width: min(92vw, 340px) !important;
    }

    .preview-map-popup .leaflet-popup-content-wrapper {
      border-radius: 18px !important;
    }

    .preview-caregiver-popup-card {
      max-height: none;
      grid-template-columns: 1fr !important;
    }

    .preview-caregiver-popup-card > img,
    .preview-caregiver-popup-card > div:first-child:not(:last-child) {
      height: 92px !important;
      min-height: 92px !important;
    }

    .preview-map-popup button,
    .preview-map-popup a,
    .preview-map-popup input,
    .preview-map-popup select,
    .preview-map-popup textarea {
      font-size: 16px !important;
    }

    .leaflet-control-attribution {
      max-width: calc(100vw - 1rem);
      white-space: normal;
    }
  }

  @media (max-width: 420px) {
    .preview-map-canvas {
      min-height: 390px !important;
    }

    .preview-map-popup .leaflet-popup-content {
      width: min(84vw, 292px) !important;
    }
  }
`;

const page = {
  minHeight: "100vh",
  padding: "18px 12px 20px",
  background:
    "radial-gradient(circle at top left, rgba(255, 207, 84, 0.16), transparent 24%), linear-gradient(180deg, #eef5f8 0%, #dfe9f2 100%)"
};

const shell = {
  maxWidth: "1600px",
  margin: "0 auto",
  display: "grid",
  gap: "12px"
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  zIndex: 1200,
  background: "rgba(12, 31, 46, 0.48)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px"
};

const modalCard = {
  width: "min(460px, 100%)",
  borderRadius: "24px",
  padding: "24px",
  background: "#ffffff",
  boxShadow: "0 28px 60px rgba(15, 23, 42, 0.22)",
  display: "grid",
  gap: "14px"
};

const modalBadge = {
  display: "inline-flex",
  alignItems: "center",
  width: "fit-content",
  minHeight: "30px",
  padding: "0 12px",
  borderRadius: "999px",
  background: "#fff3d6",
  color: "#8a5d00",
  fontSize: "12px",
  fontWeight: "800"
};

const modalTitle = {
  margin: 0,
  color: "#102a43",
  fontSize: "28px",
  lineHeight: 1.08
};

const modalText = {
  margin: 0,
  color: "#486581",
  fontSize: "15px",
  lineHeight: 1.6
};

const modalForm = {
  display: "grid",
  gap: "14px"
};

const modalInput = {
  minHeight: "52px",
  padding: "0 16px",
  borderRadius: "16px",
  border: "1px solid #cbd5e0",
  fontSize: "15px",
  color: "#102a43",
  outline: "none"
};

const modalError = {
  padding: "12px 14px",
  borderRadius: "14px",
  background: "#fff1f0",
  color: "#b42318",
  border: "1px solid #ffccc7",
  fontSize: "14px",
  lineHeight: 1.45
};

const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  flexWrap: "wrap"
};

const modalSecondaryButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  padding: "0 16px",
  borderRadius: "14px",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  background: "#ffffff",
  color: "#123a59",
  fontWeight: "800",
  fontSize: "14px",
  cursor: "pointer"
};

const modalPrimaryButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  padding: "0 16px",
  border: "none",
  borderRadius: "14px",
  background: "linear-gradient(135deg, #0e5580, #156893)",
  color: "#ffffff",
  fontWeight: "800",
  fontSize: "14px",
  cursor: "pointer"
};

const topBar = {
  position: "relative",
  zIndex: 920,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "14px",
  flexWrap: "wrap",
  overflow: "visible"
};

const logoutButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "48px",
  padding: "0 16px",
  borderRadius: "14px",
  border: "1px solid rgba(180, 35, 24, 0.16)",
  background: "#fff5f4",
  color: "#b42318",
  fontWeight: "800",
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.12)"
};

const searchWrap = {
  position: "relative",
  zIndex: 930,
  width: "min(100%, 420px)",
  overflow: "visible"
};

const bannerRow = {
  display: "grid",
  gap: "10px"
};

const errorBanner = {
  padding: "12px 14px",
  borderRadius: "16px",
  background: "#fff1f1",
  border: "1px solid #f3c2be",
  color: "#b42318",
  fontSize: "14px"
};

const successBanner = {
  padding: "12px 14px",
  borderRadius: "16px",
  background: "#edfdf3",
  border: "1px solid #bee6cb",
  color: "#188957",
  fontSize: "14px"
};

const mapCard = {
  borderRadius: "28px",
  overflow: "hidden",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  boxShadow: "0 26px 56px rgba(17, 44, 77, 0.14)"
};

const mapShell = {
  position: "relative"
};

const searchCard = {
  position: "relative",
  display: "grid",
  gap: "10px",
  padding: "14px",
  borderRadius: "22px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.16)",
  width: "100%"
};

const searchLabel = {
  color: "#123a59",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.04em",
  textTransform: "uppercase"
};

const searchInputRow = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: "10px",
  alignItems: "center"
};

const searchInput = {
  width: "100%",
  minHeight: "48px",
  padding: "0 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e0",
  background: "#ffffff",
  color: "#102a43",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const searchClearButton = {
  minHeight: "42px",
  padding: "0 14px",
  borderRadius: "12px",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  background: "#f8fbfd",
  color: "#123a59",
  fontSize: "13px",
  fontWeight: "800",
  cursor: "pointer"
};

const searchResultsPanel = {
  display: "grid",
  gap: "8px",
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  right: 0,
  maxHeight: "220px",
  overflowY: "auto",
  padding: "8px",
  borderRadius: "18px",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  background: "#ffffff",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.16)",
  zIndex: 940
};

const searchResultButton = {
  display: "grid",
  gap: "4px",
  width: "100%",
  padding: "12px 14px",
  borderRadius: "14px",
  border: "1px solid rgba(14, 85, 128, 0.1)",
  background: "#f8fbfd",
  textAlign: "left",
  cursor: "pointer"
};

const searchResultName = {
  color: "#102a43",
  fontSize: "14px",
  fontWeight: "800"
};

const searchResultMeta = {
  color: "#486581",
  fontSize: "12px",
  lineHeight: 1.45
};

const searchEmptyState = {
  padding: "4px 2px 2px",
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.5
};

const mapStyle = {
  height: "calc(100vh - 120px)",
  minHeight: "760px",
  width: "100%"
};

const zoomControlsWrap = {
  position: "absolute",
  top: "18px",
  right: "18px",
  zIndex: 700,
  display: "grid",
  gap: "10px",
  justifyItems: "end",
  pointerEvents: "none"
};

const zoomButton = {
  width: "52px",
  height: "52px",
  border: "none",
  borderRadius: "18px",
  background: "rgba(255, 255, 255, 0.96)",
  color: "#123a59",
  fontSize: "30px",
  lineHeight: 1,
  fontWeight: "700",
  boxShadow: "0 18px 32px rgba(15, 23, 42, 0.18)",
  cursor: "pointer",
  pointerEvents: "auto"
};

const zoomButtonDisabled = {
  opacity: 0.45,
  cursor: "not-allowed",
  boxShadow: "0 10px 18px rgba(15, 23, 42, 0.1)"
};

const zoomLevelBadge = {
  minHeight: "34px",
  padding: "0 12px",
  borderRadius: "999px",
  background: "rgba(12, 64, 98, 0.92)",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: "800",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 14px 24px rgba(15, 23, 42, 0.18)",
  pointerEvents: "none"
};

const sharedPopup = {
  padding: "16px",
  color: "#243b53",
  fontSize: "14px"
};

const popupCard = {
  display: "grid",
  gridTemplateColumns: "150px minmax(0, 1fr)",
  background: "#ffffff",
  width: "min(92vw, 560px)"
};

const popupPhoto = {
  width: "100%",
  height: "100%",
  minHeight: "230px",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
  background: "#f7fafc",
  padding: "8px",
  boxSizing: "border-box"
};

const popupFallback = {
  width: "100%",
  height: "100%",
  minHeight: "230px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #e0a400, #f4c542)",
  color: "#14324c",
  fontWeight: "800",
  fontSize: "52px"
};

const popupBody = {
  display: "grid",
  gap: "9px",
  padding: "14px",
  minWidth: 0
};

const popupMetaRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center"
};

const popupRoleBadge = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "30px",
  padding: "0 12px",
  borderRadius: "999px",
  background: "#fff3d6",
  color: "#8a5d00",
  fontWeight: "800",
  fontSize: "13px"
};

const popupPhoneText = {
  color: "#486581",
  fontSize: "14px",
  fontWeight: "700"
};

const popupName = {
  color: "#102a43",
  fontWeight: "800",
  fontSize: "20px",
  lineHeight: 1.06
};

const popupTitle = {
  color: "#486581",
  fontSize: "14px",
  fontWeight: "800",
  lineHeight: 1.35,
  marginTop: "-6px"
};

const detailCard = {
  padding: "10px 12px",
  borderRadius: "14px",
  background: "#f7fafc",
  color: "#243b53",
  fontSize: "13px",
  lineHeight: 1.35
};

const detailCardTitle = {
  color: "#627d98",
  fontSize: "11px",
  fontWeight: "900",
  textTransform: "uppercase",
  marginBottom: "4px"
};

const popupDetailsGrid = {
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  overscrollBehaviorInline: "contain",
  paddingBottom: "2px",
  scrollbarWidth: "thin"
};

const popupDetailItem = {
  flex: "0 0 126px",
  minWidth: "126px",
  padding: "9px 10px",
  borderRadius: "12px",
  background: "#f7fafc",
  border: "1px solid #edf2f7"
};

const popupDetailLabel = {
  display: "block",
  color: "#627d98",
  fontSize: "10px",
  fontWeight: "900",
  textTransform: "uppercase",
  marginBottom: "4px"
};

const popupDetailValue = {
  display: "block",
  color: "#102a43",
  fontSize: "12px",
  fontWeight: "800",
  lineHeight: 1.35,
  overflowWrap: "anywhere"
};

const popupTextBlock = {
  padding: "10px 12px",
  borderRadius: "14px",
  background: "#ffffff",
  color: "#243b53",
  border: "1px solid #edf2f7",
  fontSize: "13px",
  lineHeight: 1.45,
  overflowWrap: "anywhere",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden"
};

const popupTextLabel = {
  color: "#627d98",
  fontSize: "10px",
  fontWeight: "900",
  textTransform: "uppercase",
  marginBottom: "5px"
};

const experienceList = {
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  scrollbarWidth: "thin"
};

const experienceItem = {
  display: "grid",
  gap: "2px",
  flex: "0 0 160px"
};

const warningCard = {
  padding: "12px 14px",
  borderRadius: "16px",
  background: "#fff7e0",
  color: "#8a5d00",
  fontSize: "13px",
  lineHeight: 1.55,
  border: "1px solid rgba(212, 160, 23, 0.28)"
};

const popupActionRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "stretch"
};

const popupEditButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "44px",
  padding: "0 14px",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  borderRadius: "14px",
  background: "#eef7ff",
  color: "#123a59",
  fontWeight: "800",
  fontSize: "14px",
  cursor: "pointer",
  flex: "1 1 92px"
};

const popupContactButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  padding: "0 14px",
  borderRadius: "14px",
  background: "#0e5580",
  color: "#ffffff",
  fontWeight: "800",
  fontSize: "14px",
  textDecoration: "none",
  flex: "1 1 110px"
};

const popupAppointmentsButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  padding: "0 14px",
  border: "1px solid rgba(35, 178, 111, 0.18)",
  borderRadius: "14px",
  background: "#e9f9f1",
  color: "#127347",
  fontWeight: "800",
  fontSize: "14px",
  cursor: "pointer",
  flex: "1 1 130px"
};

const popupDeleteButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "44px",
  padding: "0 14px",
  border: "1px solid rgba(180, 35, 24, 0.16)",
  borderRadius: "14px",
  background: "#fff5f4",
  color: "#b42318",
  fontWeight: "800",
  fontSize: "14px",
  cursor: "pointer",
  flex: "1 1 92px"
};
